/**
 * CreatorDrilldownPage — per-creator drill-down report.
 * Lets the operator pick an influencer id + date range, and shows a summary
 * KPI card (clicks, conversions, CR bps, revenue, earned) plus a bucketed
 * timeline table, both backed by GET /api/v1/creator-drilldown/creator/:id/*.
 */
import { useMemo, useState } from "react";
import { useCreatorSummary, useCreatorTimeline } from "./hooks";
import type { BucketKind } from "./api";

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const TH =
	"px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-zinc-500";
const TD = "px-3 py-2 text-sm text-zinc-900";
const LABEL = "text-xs font-semibold uppercase tracking-wider text-zinc-500";
const INPUT =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500";

const DAY = 86_400;

function defaultRange(): { from: string; to: string } {
	const nowSec = Math.floor(Date.now() / 1000);
	const to = nowSec;
	const from = nowSec - 30 * DAY;
	return { from: String(from), to: String(to) };
}

function formatCents(cents: number): string {
	const dollars = cents / 100;
	return dollars.toLocaleString(undefined, {
 minimumFractionDigits: 2,
 maximumFractionDigits: 2,
	});
}

function formatBps(bps: number): string {
	return (bps / 100).toFixed(2) + "%";
}

function formatTs(ts: number): string {
	try {
 const d = new Date(ts * 1000);
 return d.toISOString().slice(0, 10);
	} catch {
 return String(ts);
	}
}

export function CreatorDrilldownPage() {
	const [influencerId, setInfluencerId] = useState("");
	const [range, setRange] = useState(defaultRange);
	const [bucket, setBucket] = useState<BucketKind>("day");
	const [submitted, setSubmitted] = useState<{
 influencerId: string;
 from: number;
 to: number;
 bucket: BucketKind;
	} | null>(null);

	const canSubmit = useMemo(() => {
 const f = Number(range.from);
 const t = Number(range.to);
 return (
 influencerId.trim().length > 0 &&
 Number.isFinite(f) &&
 Number.isFinite(t) &&
 f <= t
 );
	}, [influencerId, range]);

	const summaryQuery = useCreatorSummary({
 influencerId: submitted?.influencerId ?? "",
 from: submitted?.from ?? 0,
 to: submitted?.to ?? 0,
 enabled: submitted !== null,
	});

	const timelineQuery = useCreatorTimeline({
 influencerId: submitted?.influencerId ?? "",
 from: submitted?.from ?? 0,
 to: submitted?.to ?? 0,
 bucket: submitted?.bucket ?? "day",
 enabled: submitted !== null,
	});

	function onSubmit(e: React.FormEvent) {
 e.preventDefault();
 if (!canSubmit) return;
 setSubmitted({
 influencerId: influencerId.trim(),
 from: Number(range.from),
 to: Number(range.to),
 bucket,
 });
	}

	const summary = summaryQuery.data;
	const timeline = timelineQuery.data;
	const loading = submitted
 ? summaryQuery.isLoading || timelineQuery.isLoading
 : false;
	const error = summaryQuery.error ?? timelineQuery.error;

	return (
 <main
 data-testid="creator-drilldown-page"
 className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
 >
 <header className="flex flex-col gap-2">
 <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
 Report
 </p>
 <h1 className="text-green-700 font-semibold text-3xl font-semibold">
 Creator drill-down
 </h1>
 <p className="text-sm text-zinc-600">
 Zoom into a single creator's performance across any window.
 </p>
 </header>

 <section className={CARD} aria-label="Query controls">
 <form
 onSubmit={onSubmit}
 className="grid grid-cols-1 gap-4 md:grid-cols-5"
 data-testid="creator-drilldown-form"
 >
 <label className="flex flex-col gap-1 md:col-span-2">
 <span className={LABEL}>Influencer ID</span>
 <input
 className={INPUT}
 type="text"
 value={influencerId}
 onChange={(e) => setInfluencerId(e.target.value)}
 placeholder="infl_..."
 data-testid="creator-drilldown-influencer-input"
 />
 </label>
 <label className="flex flex-col gap-1">
 <span className={LABEL}>From (epoch sec)</span>
 <input
 className={INPUT}
 type="number"
 inputMode="numeric"
 value={range.from}
 onChange={(e) =>
 setRange((r) => ({ ...r, from: e.target.value }))
 }
 data-testid="creator-drilldown-from-input"
 />
 </label>
 <label className="flex flex-col gap-1">
 <span className={LABEL}>To (epoch sec)</span>
 <input
 className={INPUT}
 type="number"
 inputMode="numeric"
 value={range.to}
 onChange={(e) =>
 setRange((r) => ({ ...r, to: e.target.value }))
 }
 data-testid="creator-drilldown-to-input"
 />
 </label>
 <label className="flex flex-col gap-1">
 <span className={LABEL}>Bucket</span>
 <select
 className={INPUT}
 value={bucket}
 onChange={(e) => setBucket(e.target.value as BucketKind)}
 data-testid="creator-drilldown-bucket-select"
 >
 <option value="day">Day</option>
 <option value="week">Week</option>
 </select>
 </label>
 <div className="md:col-span-5">
 <button
 type="submit"
 disabled={!canSubmit}
 className="rounded-[6px] bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-zinc-300"
 data-testid="creator-drilldown-submit"
 >
 Load report
 </button>
 </div>
 </form>
 </section>

 {error ? (
 <p
 className="text-sm text-red-600"
 role="alert"
 data-testid="creator-drilldown-error"
 >
 {error.message}
 </p>
 ) : null}

 {loading ? (
 <p
 className="text-sm text-zinc-500"
 data-testid="creator-drilldown-loading"
 >
 Loading.
 </p>
 ) : null}

 {submitted && summary && !loading ? (
 <section
 className="grid grid-cols-2 gap-4 md:grid-cols-5"
 aria-label="Summary KPIs"
 data-testid="creator-drilldown-kpis"
 >
 <KPI label="Clicks" value={summary.clicks.toLocaleString()} />
 <KPI
 label="Conversions"
 value={summary.conversions.toLocaleString()}
 />
 <KPI label="CR" value={formatBps(summary.cr_bps)} />
 <KPI
 label="Revenue"
 value={`$${formatCents(summary.revenue_cents)}`}
 />
 <KPI
 label="Earned"
 value={`$${formatCents(summary.earned_cents)}`}
 />
 </section>
 ) : null}

 {submitted && timeline && !loading ? (
 <section className={CARD} aria-label="Timeline">
 <div className="mb-3 flex items-baseline justify-between">
 <h2 className="text-green-700 font-semibold text-sm font-semibold text-zinc-900">
 Timeline ({timeline.bucket})
 </h2>
 <span className="text-xs text-zinc-500">
 {timeline.count} bucket{timeline.count === 1 ? "" : "s"}
 </span>
 </div>
 {timeline.series.length === 0 ? (
 <p
 className="text-sm text-zinc-500"
 data-testid="creator-drilldown-timeline-empty"
 >
 No buckets.
 </p>
 ) : (
 <div className="overflow-x-auto">
 <table
 className="w-full border-collapse"
 data-testid="creator-drilldown-timeline-table"
 >
 <thead className="bg-zinc-50">
 <tr>
 <th className={TH}>Bucket</th>
 <th className={TH}>Clicks</th>
 <th className={TH}>Conversions</th>
 <th className={TH}>Revenue</th>
 <th className={TH}>Earned</th>
 </tr>
 </thead>
 <tbody className="divide-y divide-zinc-200">
 {timeline.series.map((pt) => (
 <tr
 key={pt.bucket_start}
 data-testid={`creator-drilldown-tl-row-${pt.bucket_start}`}
 >
 <td className={`${TD} tabular-nums`}>
 {formatTs(pt.bucket_start)}
 </td>
 <td className={`${TD} tabular-nums`}>
 {pt.clicks.toLocaleString()}
 </td>
 <td className={`${TD} tabular-nums`}>
 {pt.conversions.toLocaleString()}
 </td>
 <td className={`${TD} tabular-nums`}>
 ${formatCents(pt.revenue_cents)}
 </td>
 <td className={`${TD} tabular-nums`}>
 ${formatCents(pt.earned_cents)}
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 )}
 </section>
 ) : null}

 {!submitted ? (
 <p
 className="text-sm text-zinc-500"
 data-testid="creator-drilldown-empty"
 >
 Enter an influencer ID and load a report.
 </p>
 ) : null}
 </main>
	);
}

function KPI({ label, value }: { label: string; value: string }) {
	return (
 <div className={CARD}>
 <p className={LABEL}>{label}</p>
 <p className="mt-1 text-2xl font-semibold tabular-nums text-zinc-900">
 {value}
 </p>
 </div>
	);
}

export default CreatorDrilldownPage;
